import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { exhaustMap, map, filter } from 'rxjs/operators';
import { OpportunityRepository } from '../../../repository/opportunity.repository';
import { setOpportunityDataAction, setCapacityMetricsAction } from '../opportunity-config.actions';
import { updateServiceRegistrationsSuccess } from '../../service-registrations/service-registrations.actions';
import { getOpportunitySelector } from '../opportunity-config.selectors';

@Injectable()
export class GetCapacityMetricsEffect {
  constructor(
    private actions$: Actions,
    private opportunityRepo: OpportunityRepository,
    private store: Store,
  ) {}

  // When opportunity data is set, fetch remaining volunteer slots and compute capacity metrics
  getCapacityMetrics$ = createEffect(() => this.actions$.pipe(
    ofType(setOpportunityDataAction),
    exhaustMap(({ opportunity }) => {
      const maxVolunteers = Number(opportunity?.maxStudents) || 0;
      return this.fetchCapacityMetrics$(opportunity.id, maxVolunteers);
    })
  ));

  // Refresh capacity metrics when approvals/denials update registrations
  refreshOnRegistrationUpdate$ = createEffect(() => this.actions$.pipe(
    ofType(updateServiceRegistrationsSuccess),
    concatLatestFrom(({ registrations }) => this.store.pipe(select(getOpportunitySelector))),
    filter(([{ registrations }, opportunity]) => !!opportunity && !!registrations && registrations.some(r => r.oppId === opportunity.id)),
    exhaustMap(([{ registrations }, opportunity]) => {
      const maxVolunteers = Number(opportunity!.maxStudents) || 0;
      return this.fetchCapacityMetrics$(opportunity!.id, maxVolunteers);
    })
  ));

  private fetchCapacityMetrics$(opportunityId: string, maxVolunteers: number) {
    return this.opportunityRepo.getRemainingVolunteerSlots(opportunityId).pipe(
      map((remaining: number) => {
        const remainingSlots = Number(remaining) || 0;
        const volunteerCount = Math.max(0, maxVolunteers - remainingSlots);
        return setCapacityMetricsAction({ remainingVolunteers: remainingSlots, volunteerCount });
      })
    );
  }
}
